// The theme in use (01-design.md, Behaviour): the system's light or dark while signed out, the
// person's colour mode while signed in; any accent, contrast or style other than the default is
// computed here and applied at once, everywhere. Also keeps the title bar colour: the sign-in look's
// page colour on the start and signed-out screens (03, 04), the rail colour once the app is open.
import { onSettings, readSettings, writeSetting, type Settings } from '../core/settings';
import { readSession } from '../core/session';
import { DEFAULT_SEED, isDefaultLook, type Look } from './look';

const media = matchMedia('(prefers-color-scheme: dark)');
// Signed in from the first frame: the start's skeleton is in the person's own colours (03, v1.58.18).
let signedIn = !!readSession();
let appOpen = false;
let run = 0;

function currentLook(): Look {
  const s = readSettings();
  const mode = signedIn ? (s.theme ?? 'system') : 'system';
  return {
    dark: mode === 'system' ? media.matches : mode === 'dark',
    seed: s.accent ?? DEFAULT_SEED,
    contrast: s.contrast ?? 0,
    style: s['colour-style'] ?? 'tonal-spot',
  };
}

/** The look last drawn: a change of it from Settings or the system cross-fades (01, The theme in use, 6). */
let drawn = '';

/** The look's colour properties now on the page (the head script's paint-cache ones at first, 01). */
let painted: string[] = ['bg-container', 'surface-container', 'rail'];

export async function applyTheme(fade = false): Promise<void> {
  const mine = ++run;
  const look = currentLook();
  const palette = isDefaultLook(look) ? undefined : (await import('./palette')).palette;
  const tokens = palette?.(look) ?? {};
  if (mine !== run) return; // a newer choice was made meanwhile
  keepPaintCache(palette && { [look.dark ? 'dark' : 'light']: tokens, [look.dark ? 'light' : 'dark']: palette({ ...look, dark: !look.dark }) });

  const root = document.documentElement;
  const paint = () => {
    // Only the look's own colours change: what others keep on the page (the circle's colour for
    // Bloom, 02; the visible height, 15) stays.
    for (const name of painted) if (!(name in tokens)) root.style.removeProperty(`--${name}`);
    painted = Object.keys(tokens);
    // Signed out, the page keeps the sign-in look's colour the first frame had, also before anything is drawn (03, 04).
    root.style.background = signedIn ? '' : 'var(--si-page)';
    for (const [name, value] of Object.entries(tokens)) root.style.setProperty(`--${name}`, value);
    root.dataset.desyrTheme = look.dark ? 'dark' : 'light';

    const css = getComputedStyle(root);
    const rail = css.getPropertyValue('--rail').trim();
    root.dataset.desyrRailColour = rail;
    const titleBar = appOpen ? rail : css.getPropertyValue('--si-page').trim();
    document.querySelector<HTMLMetaElement>('meta[name=theme-color]')?.setAttribute('content', titleBar);
  };
  const changed = drawn !== (drawn = JSON.stringify(look));
  // Every colour from the old to the new over 200 ms, the whole window at once (global.css); at once
  // where the system asks for less motion or the browser can't.
  if (!fade || !changed || !document.startViewTransition || matchMedia('(prefers-reduced-motion: reduce)').matches) return paint();
  root.dataset.fading = '';
  await document.startViewTransition(paint).finished.catch(() => {});
  delete root.dataset.fading;
}

/**
 * Another look's page, surface and rail colours, light and dark, for the head script's first paint
 * (01, The theme in use 5, v1.58.27); none for the default look, whose colours the page carries.
 */
function keepPaintCache(tokens?: Record<string, Record<string, string>>): void {
  const pick = (t: Record<string, string>) => ({ page: t['bg-container'], surface: t['surface-container'], rail: t.rail });
  const cache: Settings['paint-cache'] = tokens && { light: pick(tokens.light), dark: pick(tokens.dark) };
  if (JSON.stringify(cache) !== JSON.stringify(readSettings()['paint-cache'])) writeSetting('paint-cache', cache);
}

/** The app's own screens are showing (not the start or signed-out screens). */
export function setAppOpen(value: boolean): void {
  if (appOpen === value) return;
  appOpen = value;
  void applyTheme();
}

/** Signed in, the person's colour mode applies; signed out, the system's. */
export function setSignedIn(value: boolean): void {
  signedIn = value;
  void applyTheme();
}

/** Applies the theme, and again on every change; resolves once the first look is in place. */
export function startTheme(): Promise<void> {
  media.addEventListener('change', () => void applyTheme(true));
  onSettings(() => void applyTheme(true));
  return applyTheme().catch(() => {});
}
